import {useEffect, useState} from 'react';
import {BackendApi} from "../../../../../api/api.ts";
import {User} from "../../../../../api/accessControl/User.ts";
import {BankAccountData, CustomerBankAccount} from "../../../../../api/customerBankAccounts/CustomerBankAccount.ts";
import BankAccountGroup from "../../../../../components/general/BankAccountGroup.tsx";


interface Props {
    currentUser: User;
    businessSettings: any;
    CreateOnSettingChangeFn: (setting: string, api: BackendApi) => (value: any) => void;
    getBusinessSetting: (name: string, businessSettings: any) => any;
}

function SettingRow({name, children, full}: { name: string, children: any, full?: boolean }) {
    return (
        <div className={("justify-between bg-gray-50 p-3 rounded-md" + (!full ? ' sm:flex' : ''))}>
            <dt className="font-medium text-gray-900 sm:w-64 sm:pr-6">
                {name}
            </dt>
            <dd className="mt-1 flex justify-between gap-x-6 sm:mt-0">
                {children}
            </dd>
        </div>
    )
}

export function InsightsSettingsTab({
                                        currentUser,
                                        businessSettings,
                                        CreateOnSettingChangeFn,
                                        getBusinessSetting
                                    }: Props) {
    const [customerBankAccounts, setCustomerBankAccounts] = useState<CustomerBankAccount[]>([]);
    const api = new BackendApi(currentUser.getToken());

    useEffect(() => {
        loadBankAccounts();
    }, []);

    async function loadBankAccounts() {
        const accounts = await api.BankAccount__myBankAccounts({business_id: currentUser.businesses[0]});
        const bankAccounts = accounts.map(account => CustomerBankAccount.fromApiResponse(account as BankAccountData).withLogoUrl(account.institution_logo_url));
        setCustomerBankAccounts(bankAccounts);
    }

    function updateMinimumCashBuffer(accountId: string, value: number) {
        const currentBuffers = getBusinessSetting('MINIMUM_CASH_BALANCE_BUFFERS', businessSettings) || {};
        const newBuffers = {
            ...currentBuffers,
            [accountId]: {minimum_cash_balance_buffer: value}
        };
        CreateOnSettingChangeFn('MINIMUM_CASH_BALANCE_BUFFERS', api)(newBuffers);
    }

    return (
        <div>
            <dl className="space-y-3 text-sm leading-6">
                <h3 className="text-xl font-gray-900 py-3 pt-6">Minimale saldo buffer per bankrekening</h3>
                <p className="text-sm text-gray-600 mb-4">
                    Vul de minimale cashbuffer in die je op deze bankrekening zou willen hebben. Als we zien dat het saldo onder dit minimum komt, sturen we je een alert.
                </p>

                {customerBankAccounts.map((account) => {
                    const currentBuffers = getBusinessSetting('MINIMUM_CASH_BALANCE_BUFFERS', businessSettings) || {};
                    const currentBuffer = currentBuffers[account.id]?.minimum_cash_balance_buffer || '';

                    return (
                        <div
                            key={account.id}
                            className="flex items-center justify-between bg-white p-4 rounded-lg border border-gray-200"
                        >
                            <div className="flex-1">
                                <BankAccountGroup
                                    bank_account_owner_name={account.ownerName}
                                    bank_logo_url={account.logoUrl}
                                    bank_account_iban={account.displayIdentifier()}
                                />
                            </div>

                            <div className="flex items-center gap-x-2">
                                <span className="text-sm text-gray-700">Minimale buffer €</span>
                                <input
                                    type="number"
                                    className="block w-32 rounded-md border-0 px-3 py-2 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6"
                                    value={currentBuffer}
                                    onChange={(e) => updateMinimumCashBuffer(account.id, Number(e.target.value))}
                                    placeholder="5000"
                                />
                            </div>
                        </div>
                    );
                })}
                <h3 className="text-xl font-gray-900 py-3 pt-6">Alert grote transacties</h3>
                <SettingRow
                    name="Notificatie drempels"
                    full={true}
                >
                    <div className="flex flex-col gap-4 w-full mt-4">
                        <p className="text-xs text-gray-600">
                            Wij sturen je een alert als er een een grote transactie is geweest op een van je
                            bankrekeneingen.
                            Je kan de drempelbedragen hier aanpassen.
                        </p>
                        <div className="flex flex-col sm:flex-row gap-6 w-full">
                            <div className="flex-1">
                                <div className="flex items-center gap-x-2">
                                    <span>Stuur voor inkomsten vanaf: €</span>
                                    <input
                                        type="number"
                                        className="block rounded-md border-0 px-3 py-2 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6"
                                        value={getBusinessSetting('LARGE_TRANSACTION_INCOME_THRESHOLD', businessSettings) || 500}
                                        onChange={(e) => CreateOnSettingChangeFn('LARGE_TRANSACTION_INCOME_THRESHOLD', api)(Number(e.target.value))}
                                        placeholder="500"
                                    />
                                </div>
                            </div>
                            <div className="flex-1">
                                <div className="flex items-center gap-x-2">
                                    <span>Stuur voor uitgaven vanaf: €</span>
                                    <input
                                        type="number"
                                        className="block rounded-md border-0 px-3 py-2 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6"
                                        value={getBusinessSetting('LARGE_TRANSACTION_EXPENSE_THRESHOLD', businessSettings) || 150}
                                        onChange={(e) => CreateOnSettingChangeFn('LARGE_TRANSACTION_EXPENSE_THRESHOLD', api)(Number(e.target.value))}
                                        placeholder="150"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </SettingRow>
            </dl>
        </div>
    );
}
